'use client'
import { Alert, AlertTitle, Box } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import * as yup from 'yup'
import ParentCard from 'src/components/era-package/shared/ParentCard'
import pathMap from 'src/paths/pathMap'
import AbiTradePartnerApiUrls from '../../../../../apiUrls/AbiTradePartnerApiUrls'
import SnackBar from '../../../../../components/era-package/era/SnackBar'
import AbiUserSettingsApiUrls from '../../../../../apiUrls/AbiUserSettingsApiUrls'
import {
  header,
  optionsMap,
  contact,
  importerInformation
} from 'src/views/abi/fields/create-trade-partner'
import RenderFormWithRHF from 'src/components/era-package/forms/RenderFormWithRHF'

// Custom validation schema for Importer conditional required fields
const tradePartnerSchema = (basicSchema) => {
  return yup.object({
    ...basicSchema,
    active: yup.mixed().when('partyCode', {
      is: 'IM',
      then: () => yup.mixed().required('Status is required'),
      otherwise: () => yup.mixed().nullable()
    }),
    taxIdNumber: yup.string().when('partyCode', {
      is: 'IM',
      then: () => yup.string().required('Tax id Number is required'),
      otherwise: () => yup.string().nullable()
    }),
    address1: yup.string().when('partyCode', {
      is: 'IM',
      then: () => yup.string().required('Address 1 is required'),
      otherwise: () => yup.string().nullable()
    }),
    city: yup.string().when('partyCode', {
      is: 'IM',
      then: () => yup.string().required('City is required'),
      otherwise: () => yup.string().nullable()
    }),
    stateProvince: yup.string().when('partyCode', {
      is: 'IM',
      then: () => yup.string().required('State/Province is required'),
      otherwise: () => yup.string().nullable()
    }),
    postalCode: yup.string().when('partyCode', {
      is: 'IM',
      then: () => yup.string().max(6).required('Postal Code is required'),
      otherwise: () => yup.string().max(6).nullable()
    }),
    countryCode: yup.string().when('partyCode', {
      is: 'IM',
      then: () => yup.string().required('Country is required'),
      otherwise: () => yup.string().nullable()
    }),
    bondType: yup.string().when('partyCode', {
      is: 'IM',
      then: () => yup.string().required('Bond Type is required'),
      otherwise: () => yup.string().nullable()
    }),
    suretyCode: yup.string().when('partyCode', {
      is: 'IM',
      then: () => yup.string().required('Surety Code is required'),
      otherwise: () => yup.string().nullable()
    })
  })
}

const Wrapper = ({ readOnly, children }) => {
  if (readOnly) return <>{children}</>
  return (
    <ParentCard title='Trade Partner Details'>
      <Alert severity='info' sx={{ mb: 2 }}>
        <AlertTitle>Note</AlertTitle>
        This form captures all required data elements. Fields marked with{' '}
        <Box component='span' color='error.main'>
          *
        </Box>{' '}
        are mandatory.
      </Alert>
      {children}
    </ParentCard>
  )
}

const EditTradePartner = ({ readOnly, id }) => {
  const history = useHistory()
  const [isFetching, setFetching] = useState(false)
  const [fetchedData, setFetchedData] = useState()
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false)
  const [showSubmitErrorSnackbar, setShowSubmitErrorSnackbar] = useState(false)
  const [userSetting, setUserSetting] = useState(null)

  const fetchUserSetting = async () => {
    try {
      const response = await fetch(`${AbiUserSettingsApiUrls.GET}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization:
            'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        }
      })

      if (!response.ok) {
        console.log('Error fetching user setting')
        return { contactName: '', contactPhone: '' }
      }

      const json = await response.json()
      return json.data
    } catch (error) {
      console.log('Error fetching user setting')
      return { contactName: '', contactPhone: '' }
    }
  }

  // TODO set defaultValues combined, async
  useEffect(() => {
    let isAlive = true

    fetch(AbiTradePartnerApiUrls.GET + `?id=${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      }
    })
      .then((res) => res.json())
      .then((json) => {
        if (isAlive && !!json.data) {
          let parse = json.data
          console.log(parse)
          fetchUserSetting().then((r) => {
            if (parse.bondExpirationDate) {
              parse.bondExpirationDate = new Date(
                parse.bondExpirationDate
              )
            }
            setFetchedData(parse)
            setFetching(true)
          })
        }
      })
      .catch((err) => {
        console.error(err)
        setFetching(true)
      })

    return () => {
      isAlive = false
      setFetching(true)
    }
  }, [])

  // Set up form sections including table sections
  const formSections = useMemo(
    () => [
      {
        fields: header,
        headless: true,
        disableGutters: true
      },
            {
        title: 'Contact',
        fields: contact,
        disableGutters: false
      },
      {
        title: 'Importer Information',
        fields: importerInformation,
        disableGutters: true,
        condition: (formData) => formData.partyCode === 'IM'
      }
    ],
    []
  )

  // Set up form data
  const allFields = useMemo(
    () =>
      formSections.flatMap((section) => {
        console.log(userSetting?.filerCode)
        section.fields.map(
          (f) =>
            (f.defaultValue =
              f.name === 'filerCode' ? userSetting?.filerCode : '')
        )
        return section.fields
      }),
    [formSections, userSetting] // Make sure to update the dependency array
  )

  const handleSubmit = async (formData) => {
    try {
      const response = await fetch(AbiTradePartnerApiUrls.PUT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization:
            'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
        body: JSON.stringify(formData) // Convert JS object to JSON string
      })
      if (!response.ok) {
        console.error('Error fetching user data')
        setShowSubmitErrorSnackbar(true)
        return false
      }
      const result = await response.json()
      setShowSuccessSnackbar(formData.id)
      console.log('submitted:', result)
      return true
    } catch (error) {
      console.error('Error fetching user data:', error)
    }
  }

  // Handle snackbar close
  const handleSuccessSnackbarClose = (e, reason) => {
    if (reason === 'timeout') {
      history.push(
        pathMap.ABI_TRADE_PARTNER_VIEW.replace(
          ':id',
          showSuccessSnackbar
        )
      )
      setShowSuccessSnackbar(false)
    }
  }

  // Handle snackbar close
  const handleSubmitErrorSnackbarClose = useCallback(() => {
    setShowSubmitErrorSnackbar(false)
  }, [])

  if (!isFetching) return <p>Loading user data...</p>

  return (
    <Wrapper readOnly={readOnly}>
            <RenderFormWithRHF
        title='Basic Information'
        formSections={formSections}
        allFields={allFields}
        optionsMap={optionsMap}
        submitLabel='Update'
        onSubmit={handleSubmit}
        sx={{ border: 0 }}
        formConfig={{}}
        initialData={fetchedData}
        readOnly={readOnly}
        hideSave={true}
        schema={tradePartnerSchema}
      />
      {/* Error Snackbar - Only show when showErrorSnackbar is true */}
      <SnackBar
        title='Form Submittion Error'
        type='error'
        open={showSubmitErrorSnackbar}
        onClose={handleSubmitErrorSnackbarClose}
        duration={10000}
      >
        Internal error occured, please try again later.
      </SnackBar>

      {/* Success Snackbar - Only show when showSuccessSnackbar is true */}
      <SnackBar
        title='Form submitted successfully'
        type='success'
        open={!!showSuccessSnackbar}
        onClose={handleSuccessSnackbarClose}
        duration={3000}
      >
        Redirecting to Trade Partner Summary...
      </SnackBar>
    </Wrapper>
  )
}

export default EditTradePartner
